import { useFormikContext } from 'formik';
import React from 'react';
import * as styles from './SSOTab.module.scss';
import useAppSelector from '../../app/redux/useAppSelector';
import { Field, FieldSet } from '../../components/Form';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import type { IntegrationPlatformInfo } from '../../integrations/IntegrationPlatformInfo';

const INTEGRATION_FRAGMENT = gql(`
  fragment SSOTab_integration on Integration {
    id
    platform
    features {
      saml
    }
    settings {
      enableSso
      idpMetadata
    }
  }
`);

interface SSOTabProps {
  integration: DocumentType<typeof INTEGRATION_FRAGMENT>;
  platformInfo: IntegrationPlatformInfo;
}

const SSOTab = ({
  integration: { platform, features },
  platformInfo,
}: SSOTabProps) => {
  const { values } = useFormikContext<{ enableSso: boolean }>();
  const orgId = useAppSelector((state) => state.session.organization.id);

  // TODO: Remove platform check to enable Microsoft SAML on frontend
  const isShowSamlOptions =
    features?.saml === 'ENABLED' && platform !== 'microsoft';

  return (
    <FieldSet>
      <Field
        name="enableSso"
        type="checkbox"
        label={`Enable ${platformInfo.name} Single Sign-On`}
      />
      {isShowSamlOptions && (
        <>
          <div className={styles.title}>Your Org ID</div>
          <div className={styles.content}>
            Your team&apos;s unique Org ID is: <strong>{orgId}</strong>
          </div>

          <Field
            className={styles.idpMetadata}
            name="idpMetadata"
            type="textarea"
            label="IDP Metadata"
            disabled={!values.enableSso}
            required={values.enableSso}
          />
        </>
      )}
    </FieldSet>
  );
};

SSOTab.getFormValues = ({
  settings,
}: DocumentType<typeof INTEGRATION_FRAGMENT>) => ({
  enableSso: settings?.enableSso || false,
  idpMetadata: settings?.idpMetadata || '',
});
SSOTab.getNewSettings = (values: ReturnType<typeof SSOTab.getFormValues>) => ({
  enableSso: values.enableSso,
  idpMetadata: values.idpMetadata,
});

export default SSOTab;
